/**
 * useRealtimeInvalidation
 *
 * Subscribes to the signed-in user's private Supabase broadcast channel
 * (`user:{userId}`) and invalidates the relevant React Query caches when the
 * server signals a change — so notifications and chat update instantly instead of
 * waiting for the (now slow, fallback-only) poll. The channel carries no PII; data
 * is still fetched through the authenticated REST API on invalidation.
 */
import { useEffect } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/authStore";

interface ChangePayload {
  entity?: "notification" | "message" | "conversation" | "support" | "driver_location";
}

/** Subscribe-and-invalidate for the current user. No-op when signed out. */
export function useRealtimeInvalidation(): void {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);

  useEffect(() => {
    if (!userId) return;
    let channel: RealtimeChannel | null = null;
    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      if (!token || cancelled) return;
      // Authorize the realtime socket so the private-channel RLS policy passes.
      void supabase.realtime.setAuth(token);
      channel = supabase
        .channel(`user:${userId}`, { config: { private: true } })
        .on("broadcast", { event: "change" }, (msg) => {
          const entity = (msg["payload"] as ChangePayload | undefined)?.entity;
          if (entity === "notification") {
            void queryClient.invalidateQueries({ queryKey: ["notifications"] });
          } else if (entity === "message") {
            void queryClient.invalidateQueries({ queryKey: ["conversation-messages"] });
            void queryClient.invalidateQueries({ queryKey: ["conversations"] });
            void queryClient.invalidateQueries({ queryKey: ["notifications"] });
          } else if (entity === "conversation") {
            void queryClient.invalidateQueries({ queryKey: ["conversations"] });
          } else if (entity === "driver_location") {
            // L5 (epic #559): a driver breadcrumb landed. The signal carries no
            // coordinates — refetch the public reservation read so its additive
            // `driverEta` (L3) refreshes for the customer. (The operator fleet-map
            // consumer subscribes to its own `company:{id}` channel; see #613.)
            void queryClient.invalidateQueries({ queryKey: ["public-reservation"] });
          }
        })
        .subscribe();
    });

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}
